import {
  PackageDetails,
  PackageVersion,
} from "../../layouts/booking-create-page/booking-create-page.types";
import type { PoType } from "@/features/license-fee/list/utils/private-office-contract-mappings";

export enum DefaultPoContractsUpdateFlowStep {
  UPDATE = "PO_CONTRACTS_UPDATE",
  SUMMARY = "PO_CONTRACTS_UPDATE_SUMMARY",
  LOADING = "PO_CONTRACTS_UPDATE_LOADING",
  RESULT = "PO_CONTRACTS_UPDATE_RESULT",
}
export enum UpsellPoContractsUpdateFlowStep {
  UPDATE = "UPSELL_PO_CONTRACTS_UPDATE",
  UPSELL = "UPSELL_PO_CONTRACTS_UPSELL",
  SUMMARY = "UPSELL_PO_CONTRACTS_SUMMARY",
  LOADING = "UPSELL_PO_CONTRACTS_LOADING",
  RESULT = "UPSELL_PO_CONTRACTS_RESULT",
}
export type PoContractsUpdateFlowStep =
  | DefaultPoContractsUpdateFlowStep
  | UpsellPoContractsUpdateFlowStep;

export type FlowType = "default" | "upsell";

export type PoContractsUpdateFlowStatus = {
  update: "success" | "error" | null;
  upsell: "success" | "error" | null;
};

export type ProcessingStatus = {
  isProcessing: boolean;
  canNavigateAway: boolean;
  currentOperation: "update" | "upsell" | null;
};

export type PoContractUpdateData = {
  id: number;
  company: {
    id: number;
    name: string;
  };
  space: {
    id: number;
    name: string;
  };
  privateOffice: {
    value: number;
    label: string;
    monthlyPrice: number;
  };
  acceptanceTerm: string;
  knowledgeTerm: string;
  contractResponsibleUserId: number;
  contractResponsibleUser: { name: string; id: number };
  contractType: string;
  startDate: string;
  endDate: string;
  dealId: number;
  expectedDurationMonths: number;
  flowType: FlowType;
  hasUndeterminedTime?: boolean;
  members: number;
  monthlyCredits: number;
  notes: string;
  noticeInDaysClient: number;
  noticeInDaysPartner: number;
  partnerValue: string;
  packageId: number;
  renewalIndicator: string;
  repass: number;
  roomResponsibleUserId: number;
  roomResponsibleUser: { name: string; id: number };
  takeRatePercentage: number;
  acceptanceTermFiles?: { id: number; url: string; name: string };
  knowledgeTermFiles?: { id: number; url: string; name: string };
  poType?: PoType;
};

export type FieldsToUpdate =
  | (Partial<PoContractUpdateData> & {
      setToIndeterminate: boolean;
      takePercentage: number;
      partnerPrice: number;
    })
  | Record<string, never>;

export type PoInitialContractData = {
  dealId: string;
  packageId: number;
  contractResponsibleUser: { name: string; id: number };
  contractResponsibleUserId: number;
  roomResponsibleUser?: { name?: string; id?: number };
  roomResponsibleUserId: number;
  members: number;
  startDate: string;
  endDate?: string;
  monthlyCredits: number;
  takeRatePercentage: number;
  repass: number;
  noticeInDaysClient: number;
  noticeInDaysPartner: number;
  expectedDurationMonths: number;
  renewalIndicator: string;
  acceptanceTermFiles?: { id: number; url: string; name: string };
  knowledgeTermFiles?: { id: number; url: string; name: string };
  acceptanceTerm: string;
  knowledgeTerm: string;
  partnerValue: string;
  hasUndeterminedTime: boolean;
  contractType: string;
  notes: string;
  poType?: PoType;
};

export type SimulatedUpsellResponse = {
  data: {
    newPayments: simulatedPayment[] | [];
    updatedCycles: simulatedUpdatedCycles[];
  };
};

export type SimulateUpsellProps = {
  creditAmount: number;
  beginDate: string;
  companyId: number;
  packageId: number;
};

export type SimulatedUpdate = {
  toCreate: {
    count: number;
    hasProRata: boolean;
  };
  toUpdate: {
    count: number;
  };
  toCancel: {
    count: number;
  };
};

export type SimulatedUpsell = {
  newPayments: simulatedPayment[] | [];
  updatedCycles: simulatedUpdatedCycles[];
};

export interface simulatedPayment {
  cycleId: number;
  amount: number;
  capacity: number;
  paymentDate: string;
  competencyMonth: string;
  invoicingCompetencyDate?: string;
  proRata: boolean | undefined;
}

export interface simulatedUpdatedCycles {
  cycleId: number;
  oldCapacity: number;
  oldAmount: number;
  newCapacity: number;
  newAmount: number;
  startDate: string;
  endDate: string;
  proRata: boolean | undefined;
}

export type UpsellData = {
  upsellOption: number;
  upsellProRataOption?: number;
  simulatedUpsellData: SimulatedUpsell;
  beginDate: string;
  startCycle?: { id: number; startDate: string };
};

export type ErrorMessages = {
  update: string | null;
  upsell: string | null;
};

export type PoContractsUpdateFlowContextData = {
  currentStep: PoContractsUpdateFlowStep;
  setCurrentStep: (step: PoContractsUpdateFlowStep) => void;
  nextStep: () => void;
  handleSubmitPoContractUpdate: (data: PoContractUpdateData) => void;
  poContractUpdateData: PoContractUpdateData | null;
  poInitialContractData: PoInitialContractData | null;
  setPoInitialContractData: (data: PoInitialContractData) => void;
  SimulateUpsell: (data: SimulateUpsellProps) => void;
  simulatedUpsell: SimulatedUpsell | null;
  UpdateUpsellPayments: (payments: simulatedPayment[]) => void;
  UpdateUpsellCycles: (cycles: simulatedUpdatedCycles[]) => void;
  upsellData: UpsellData | null;
  setUpsellData: (data: UpsellData) => void;
  previousStep: () => void;
  flowType: FlowType;
  setSelectedCycle: (cycle: PackageVersion) => void;
  selectedCycle: PackageVersion | null;
  setSelectedPackage: (packageDetails: PackageDetails) => void;
  selectedPackage: PackageDetails | null;
  setSelectedPackageGroups: (groups: { id: number; name: string }[]) => void;
  selectedPackageGroups: { id: number; name: string }[] | [];
  poContractsUpdateFlowStatus: PoContractsUpdateFlowStatus;
  setPoContractsUpdateFlowStatus: (
    status: Partial<PoContractsUpdateFlowStatus>,
  ) => void;
  errorMessages: ErrorMessages;
  setErrorMessages: (message: ErrorMessages) => void;
  processingStatus: ProcessingStatus;
  setProcessingStatus: (status: ProcessingStatus) => void;
  resetFlow: () => void;
  moveToSummaryStep: () => void;
  SimulateUpdate: () => void;
  simulatedUpdate: SimulatedUpdate;
};

export const FlowTransitions: Record<
  PoContractsUpdateFlowStep,
  {
    next: PoContractsUpdateFlowStep;
    previous?: PoContractsUpdateFlowStep;
  }
> = {
  [DefaultPoContractsUpdateFlowStep.UPDATE]: {
    next: DefaultPoContractsUpdateFlowStep.SUMMARY,
  },
  [DefaultPoContractsUpdateFlowStep.SUMMARY]: {
    next: DefaultPoContractsUpdateFlowStep.LOADING,
    previous: DefaultPoContractsUpdateFlowStep.UPDATE,
  },
  [DefaultPoContractsUpdateFlowStep.LOADING]: {
    next: DefaultPoContractsUpdateFlowStep.RESULT,
    previous: DefaultPoContractsUpdateFlowStep.SUMMARY,
  },
  [DefaultPoContractsUpdateFlowStep.RESULT]: {
    next: DefaultPoContractsUpdateFlowStep.RESULT,
    previous: DefaultPoContractsUpdateFlowStep.LOADING,
  },
  [UpsellPoContractsUpdateFlowStep.UPDATE]: {
    next: UpsellPoContractsUpdateFlowStep.UPSELL,
  },
  [UpsellPoContractsUpdateFlowStep.UPSELL]: {
    next: UpsellPoContractsUpdateFlowStep.SUMMARY,
    previous: UpsellPoContractsUpdateFlowStep.UPDATE,
  },
  [UpsellPoContractsUpdateFlowStep.SUMMARY]: {
    next: UpsellPoContractsUpdateFlowStep.LOADING,
    previous: UpsellPoContractsUpdateFlowStep.UPSELL,
  },
  [UpsellPoContractsUpdateFlowStep.LOADING]: {
    next: UpsellPoContractsUpdateFlowStep.RESULT,
    previous: UpsellPoContractsUpdateFlowStep.SUMMARY,
  },
  [UpsellPoContractsUpdateFlowStep.RESULT]: {
    next: UpsellPoContractsUpdateFlowStep.RESULT,
    previous: UpsellPoContractsUpdateFlowStep.LOADING,
  },
};
